import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { Select } from 'antd';
import { AdminEntityMergeRequestItemProps } from '../../AdminEntityMergeRequestItem';
import AdminMergeItemBasicConflictField from '../../components/basicConflictField/AdminMergeItemBasicConflictField';
import AdminMergeItemBasicField from '../../components/basicField/AdminMergeItemBasicField';
import {
  MergeItemSiteSegmentationFragment,
  MergeSiteRequestSegmentationQuery,
  useMRSiteSetSegmentationCodeMutation,
} from '../../../../../../../../../gql/typings';
import { useLocalization } from '../../../../../../../../util/useLocalization';
import { Locale } from '../../../../../../../../../localization/LocalizationKeys';

const AdminMergeItemSiteSegmentation: React.FC<AdminEntityMergeRequestItemProps> = (props) => {
  const info = props.item.type as MergeItemSiteSegmentationFragment;
  const localization = useLocalization();
  const { data, loading } = useQuery<MergeSiteRequestSegmentationQuery>(DATA_QUERY);
  const [setNewValue] = useMRSiteSetSegmentationCodeMutation();

  if (!props.item.hasConflict) return (
    <AdminMergeItemBasicField {...props}>
      {localization.formatMessage(Locale.Text.Merge_request_segmentation_code_mismatch_description, {
        code: <strong>{info.loser.heading}</strong>,
      })}
    </AdminMergeItemBasicField>
  );

  return (
    <AdminMergeItemBasicConflictField
      {...props}
      fromValue={info.loser.id}
      toValue={info.winner?.id}
      valueDisplayMapper={id => data?.segmentationCodes?.nodes?.filter(n => n.id === id)?.[0]?.heading}
      inputRenderer={props => (
        <Select
          {...props}
          loading={loading}
          options={(data?.segmentationCodes?.nodes ?? []).map(n => ({ value: n.id, label: n.heading }))}
        />
      )}
      updateMutation={newValue => setNewValue({
        variables: {
          siteId: props.into.id,
          segmentationCodeId: newValue,
        },
      })}
    >
      {localization.formatMessage(Locale.Text.Merge_request_person_segmentation_code_conflict_mismatch_description, {
        segmentation1: <strong>{info.loser.heading}</strong>,
        segmentation2: <strong>{info.winner?.heading}</strong>,
      })}
    </AdminMergeItemBasicConflictField>
  );
};

gql`
  mutation MRSiteSetSegmentationCode($siteId: Int!, $segmentationCodeId: Int!) {
    upsertSiteSegmentationCode(siteId: $siteId, segmentationCodeId: $segmentationCodeId) {
      id
    }
  }
`;

const DATA_QUERY = gql`
  query MergeSiteRequestSegmentation {
    segmentationCodes {
      hash
      nodes {
        id
        heading
      }
    }
  }
`;

gql`
  fragment MergeItemSiteSegmentation on SiteSegmentation_EntityDataToBeTransferredType {
    code
    dataType
    entityType
    loser {
      id
      heading
    }
    winner {
      id
      heading
    }
  }
`;

export default AdminMergeItemSiteSegmentation;
